import type { JsonSchema } from '@shared/types'
import type { McpAction, McpTool, McpToolsResponse } from 'v2/pages/settings/types'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import type { AiActionNodeData } from '../types'

interface ActionParams {
  actionName: string
  appId: string | undefined | null
  activeApps: RExternalOrgApp[]
  mcpToolsByOrgAppUuid: Record<string, McpTool>
}

export const encodeActionIdentifier = (actionName: string, appId: string) =>
  `${encodeURIComponent(actionName)}|${encodeURIComponent(appId)}`

export const decodeActionIdentifier = (
  encoded: string,
): { actionName: string; appId: string } | null => {
  const split = encoded.split('|')
  if (split.length !== 2) return null
  try {
    return {
      actionName: decodeURIComponent(split[0]),
      appId: decodeURIComponent(split[1]),
    }
  } catch (_err) {
    logger.warn(`Unable to decode action identifier: ${encoded}`)
    return null
  }
}

const findAction = ({ actionName, appId, activeApps, mcpToolsByOrgAppUuid }: ActionParams) => {
  if (!appId) return null
  const appAction = activeApps.find((app) => app.id === appId)
  if (appAction?.actions) {
    const action = appAction.actions.find((a) => a.action_name === actionName)
    if (action) return { action, type: 'app' as const }
  }
  const mcpTool = mcpToolsByOrgAppUuid[appId]
  if (mcpTool?.actions) {
    const action = mcpTool.actions.find((a) => a.name === actionName)
    if (action) return { action, type: 'mcp' as const }
  }
  return null
}

export const getActionDescription = (params: ActionParams): string => {
  if (!params.actionName || !params.appId) return ''
  const result = findAction(params)
  return result?.action.description || ''
}

export const convertDefaultValueToString = (value: unknown): string => {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'boolean' || typeof value === 'number') return value.toString()
  return String(value)
}

export const getMcpToolsByOrgAppUuid = (
  mcpTools?: McpToolsResponse,
  activeApps?: RExternalOrgApp[],
) => {
  if (!mcpTools || !activeApps) return {}
  // The API response is already keyed by orgAppUuid, so filter to only include active apps
  const activeAppIds = new Set(activeApps.map((app) => app.id))
  const mapping: Record<string, (typeof mcpTools)[string]> = {}
  Object.entries(mcpTools).forEach(([orgAppUuid, mcpTool]) => {
    if (activeAppIds.has(orgAppUuid)) {
      mapping[orgAppUuid] = mcpTool
    }
  })
  return mapping
}

export const transformAiActionConfig = (config: AiActionNodeData['config']) => {
  if (!config.actions) return config
  const transformedActions = config.actions.map((action) => {
    if (!action.action_inputs) return action
    const filteredInputs: Record<string, string> = {}
    let hasValues = false
    Object.entries(action.action_inputs).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        filteredInputs[key] = value
        hasValues = true
      }
    })
    return { ...action, action_inputs: hasValues ? filteredInputs : {} }
  })
  return { ...config, actions: transformedActions }
}

export const extractFieldsFromSchema = (schema: JsonSchema | McpAction['input_schema']) => {
  if (!schema?.properties) return { required: [], optional: [] }
  const { properties } = schema
  const required = schema.required || []
  const requiredFields = Object.keys(properties).filter((field) => required.includes(field))
  const optionalFields = Object.keys(properties).filter((field) => !required.includes(field))
  return { required: requiredFields, optional: optionalFields, schema }
}

export const getFieldDefaultValue = (params: ActionParams, fieldName: string): string => {
  const result = findAction(params)
  if (!result?.action.input_schema?.properties) return ''
  const fieldProperty = result.action.input_schema.properties[fieldName] as Record<string, unknown>
  if (!fieldProperty) return ''
  const defaultValue = fieldProperty.default || fieldProperty.defaultValue
  return convertDefaultValueToString(defaultValue)
}

export const initializeActionDataForForm = (params: ActionParams) => {
  const result = findAction(params)
  if (!result?.action.input_schema) {
    return {
      actionInputs: {},
      actionFieldTypes: { required_fields: {}, optional_fields: {} },
    }
  }
  const { required: requiredFields, optional: optionalFields } = extractFieldsFromSchema(
    result.action.input_schema,
  )
  return {
    actionInputs: {},
    actionFieldTypes: {
      required_fields: Object.fromEntries(requiredFields.map((field) => [field, 'auto' as const])),
      optional_fields: Object.fromEntries(
        optionalFields.map((field) => [field, 'manual' as const]),
      ),
    },
  }
}
